<template>
  <div class="lab">
    <form class="lab-form" @submit.prevent="runLab">
      <input
        v-model="labQuery"
        class="lab-input"
        type="text"
        autocomplete="off"
        spellcheck="false"
        placeholder="Try a question"
        aria-label="Question for the search comparison"
      >
      <button type="submit" class="lab-go" :disabled="labBusy">{{ labBusy ? '…' : 'Compare' }}</button>
    </form>
    <div class="lab-samples">
      <button
        v-for="s in labSamples"
        :key="s"
        type="button"
        class="lab-chip"
        :disabled="labBusy"
        @click="labQuery = s; runLab();"
      >{{ s }}</button>
    </div>
    <p v-if="labStatus" class="lab-status" role="status">{{ labStatus }}</p>

    <div v-if="fusedTop.length" class="lab-columns">
      <div class="lab-col">
        <p class="lab-col-title">Word match</p>
        <p class="lab-col-sub">lexical ranking</p>
        <ol>
          <li v-for="r in lexTop" :key="`l${r.id}`">
            <a class="lab-hit-title" :href="r.href" :target="r.document ? '_blank' : null" :rel="r.document ? 'noopener' : null" @click="openResult($event, r)">{{ r.title }}<ArrowUpRightIcon /></a>
            <span class="lab-bar"><span class="lab-bar-fill" :style="{ width: r.pct + '%' }"></span></span>
          </li>
          <li v-if="!lexTop.length" class="lab-none">No word overlap at all.</li>
        </ol>
      </div>
      <div class="lab-col">
        <p class="lab-col-title">Meaning match</p>
        <p class="lab-col-sub">semantic similarity</p>
        <ol>
          <li v-for="r in semTop" :key="`s${r.id}`">
            <a class="lab-hit-title" :href="r.href" :target="r.document ? '_blank' : null" :rel="r.document ? 'noopener' : null" @click="openResult($event, r)">{{ r.title }}<ArrowUpRightIcon /></a>
            <span class="lab-bar"><span class="lab-bar-fill" :style="{ width: r.pct + '%' }"></span></span>
          </li>
        </ol>
      </div>
      <div class="lab-col lab-col-fused">
        <p class="lab-col-title">Combined</p>
        <p class="lab-col-sub">what Search uses</p>
        <ol>
          <li v-for="r in fusedTop" :key="`f${r.id}`">
            <a class="lab-hit-title" :href="r.href" :target="r.document ? '_blank' : null" :rel="r.document ? 'noopener' : null" @click="openResult($event, r)">{{ r.title }}<ArrowUpRightIcon /></a>
            <span class="lab-badges">{{ r.badge }}</span>
          </li>
        </ol>
      </div>
    </div>

    <div v-if="scores.length" class="floor-demo">
      <div class="floor-strip" aria-hidden="true">
        <span
          v-for="(s, i) in scores"
          :key="i"
          class="floor-dot"
          :class="{ pass: s >= floor }"
          :style="{ left: scoreX(s) + '%' }"
        ></span>
        <span class="floor-line" :style="{ left: scoreX(floor) + '%' }"></span>
      </div>
      <div class="floor-controls">
        <input v-model.number="floor" type="range" min="0" max="0.6" step="0.005" aria-label="Minimum relevance">
        <p class="floor-readout" aria-live="polite">
          minimum relevance {{ floor.toFixed(3) }} · <strong>{{ passCount }}</strong> of {{ scores.length }} passages
          remain<span v-if="Math.abs(floor - defaultFloor) > 0.002"> (the site normally uses {{ defaultFloor }})</span>
        </p>
      </div>
    </div>
    <p v-else-if="scoresAttempted" class="floor-empty">Run a query above and the score distribution appears here.</p>
  </div>
</template>

<script>
import { loadSearchItems, lexicalRank, rrfFuse } from '../../utils/askRetrieval';
import { ensureReady, semanticScores, SCORE_FLOOR } from '../../utils/semanticSearch';
import { sourceRouteTarget } from '../../utils/viewSourceTarget';
import { settleScrollToId } from '../../utils/anchorLanding';
import ArrowUpRightIcon from '../ui/ArrowUpRightIcon.vue';

let rememberedComparison = null;
const comparisonKeys = ['labQuery', 'labStatus', 'lexTop', 'semTop', 'fusedTop', 'scores', 'scoresAttempted', 'floor'];

export default {
  name: 'RetrievalLab',
  components: { ArrowUpRightIcon },
  props: {
    initialQuery: { type: String, default: '' },
    rememberComparison: { type: Boolean, default: false },
  },
  data() {
    return {
      labQuery: this.initialQuery,
      labBusy: false,
      labStatus: '',
      labSamples: ['learning new words every day', 'HSBC', 'where has Henry lived?'],
      lexTop: [],
      semTop: [],
      fusedTop: [],
      scores: [],
      scoresAttempted: false,
      floor: SCORE_FLOOR,
      defaultFloor: SCORE_FLOOR,
      corpusSize: 0,
      ...(this.rememberComparison ? rememberedComparison : null),
    };
  },
  computed: {
    passCount() {
      return this.scores.filter((s) => s >= this.floor).length;
    },
  },
  mounted() {
    loadSearchItems().then((items) => { this.corpusSize = items.length; }).catch(() => {});
    if (!this.fusedTop.length && this.labQuery.trim()) this.runLab();
  },
  beforeUnmount() {
    if (this.rememberComparison) rememberedComparison = Object.fromEntries(comparisonKeys.map(key => [key, this[key]]));
  },
  methods: {
    resultDestination(source) {
      const target = sourceRouteTarget(source.route, source.anchor);
      return { target, href: target.href || this.$router.resolve(target).href, document: Boolean(target.href) };
    },
    openResult(event, result) {
      if (result.document || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopPropagation();
      if (result.target.path === this.$route.path && result.target.hash) {
        settleScrollToId(result.target.hash.slice(1));
      } else {
        this.$router.push(result.target).catch(() => {});
      }
    },
    scoreX(s) {
      const lo = -0.05;
      const hi = 0.7;
      return Math.max(0, Math.min(100, ((s - lo) / (hi - lo)) * 100));
    },
    cleanTitle(t) {
      return String(t || '').replace(/\s+\(\d+\)$/, '');
    },
    async runLab() {
      const q = this.labQuery.trim();
      if (!q || this.labBusy) return;
      this.labBusy = true;
      this.scoresAttempted = true;
      this.labStatus = 'Preparing meaning search. The first use downloads a model to your browser…';
      try {
        const items = await loadSearchItems();
        this.corpusSize = items.length;
        const byId = {};
        for (const it of items) byId[it.id] = it;

        await ensureReady();
        this.labStatus = '';
        const all = await semanticScores(q);
        const lex = lexicalRank(q, items);
        const fusedIds = rrfFuse(lex, all.slice(0, 12).map((h) => ({ id: h.id }))).slice(0, 8);

        const lexRank = new Map(lex.map((x, i) => [x.id, i + 1]));
        const semRank = new Map(all.map((x, i) => [x.id, i + 1]));
        const maxLex = lex.length ? lex[0].s : 1;
        const maxSem = all.length ? Math.max(all[0].score, 0.001) : 1;

        this.lexTop = lex.slice(0, 5).map((x) => ({
          ...this.resultDestination(byId[x.id]),
          id: x.id,
          title: this.cleanTitle(byId[x.id] && byId[x.id].title),
          pct: Math.round((x.s / maxLex) * 100),
        }));
        this.semTop = all.slice(0, 5).map((x) => ({
          ...this.resultDestination(byId[x.id]),
          id: x.id,
          title: this.cleanTitle(byId[x.id] && byId[x.id].title),
          pct: Math.round((Math.max(x.score, 0) / maxSem) * 100),
        }));
        this.fusedTop = fusedIds.slice(0, 5).map((id) => ({
          ...this.resultDestination(byId[id]),
          id,
          title: this.cleanTitle(byId[id] && byId[id].title),
          badge: [lexRank.has(id) ? `Word rank ${lexRank.get(id)}` : '', `Meaning rank ${semRank.get(id)}`].filter(Boolean).join(' · '),
        }));
        this.scores = all.map((x) => x.score);
      } catch (e) {
        this.labStatus = 'Meaning match is temporarily unavailable. Try again in a moment.';
      } finally {
        this.labBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.lab {
  --lab-ink: #333330;
  --lab-muted: var(--site-text-secondary);
  --lab-rule: rgb(76 70 56 / 16%);
  container: retrieval-lab / inline-size;
  box-sizing: border-box;
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--lab-rule);
  border-radius: 5px;
  background: rgb(120 111 88 / 4%);
  color: var(--lab-ink);
}
.lab-form { display: flex; gap: 8px; }
.lab-input { flex: 1; min-width: 0; width: 0; padding: 10px 12px; border: 1px solid var(--lab-rule); border-radius: 3px; background: transparent; color: inherit; font: 15px/1.5 'Quattrocento Sans', sans-serif; }
.lab-input:focus { outline: none; border-color: var(--site-accent); }
.lab-input::placeholder { color: var(--lab-muted); opacity: 1; }
.lab-go { flex: none; min-height: 44px; min-width: 86px; padding: 8px 14px; border: 1px solid var(--lab-rule); border-radius: 3px; background: transparent; color: inherit; font: 14px/1.5 'Quattrocento Sans', sans-serif; cursor: var(--site-interactive-cursor, pointer); }
.lab-samples { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 7px; }
.lab-chip { max-width: 100%; padding: 7px 0; border: 0; background: none; color: var(--lab-muted); font: 13px/1.4 'Quattrocento Sans', sans-serif; text-align: left; cursor: var(--site-interactive-cursor, pointer); }
.lab-go:disabled, .lab-chip:disabled { cursor: default; opacity: .6; }
.lab-go:focus-visible, .lab-chip:focus-visible { outline: 1px solid var(--site-accent); outline-offset: 3px; }
.lab .lab-status { margin: 12px 0 0; font: 14px/1.5 'Quattrocento Sans', sans-serif; color: var(--lab-muted); }
.lab-columns { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: clamp(16px, 3cqw, 32px); margin-top: 24px; }
.lab-col { min-width: 0; }
.lab .lab-col-title { margin: 0; font: 400 18px/1.4 'PT Serif', Georgia, serif; color: var(--lab-ink); }
.lab .lab-col-sub { margin: 3px 0 16px; font: 13px/1.4 'Quattrocento Sans', sans-serif; color: var(--lab-muted); }
.lab-col ol { margin: 0; padding-left: 1.3rem; }
.lab-col li { margin: 0 0 14px; padding-left: 3px; font: 14px/1.5 'Quattrocento Sans', sans-serif; }
.lab-col li::marker { color: var(--lab-muted); font-size: 12px; }
.lab-hit-title { display: block; padding: 3px 0; color: inherit; text-decoration: none; overflow-wrap: anywhere; }
.lab-hit-title .site-arrow-up-right { width: 11px; height: 11px; margin-left: 6px; opacity: .45; transition: transform 120ms ease, opacity 120ms ease; }
.lab-hit-title:focus-visible { outline: 1px solid var(--site-accent); outline-offset: 3px; }
.lab-none { list-style: none; margin-left: -1.3rem; color: var(--lab-muted); }
.lab-bar { display: block; height: 2px; margin-top: 7px; background: var(--lab-rule); overflow: hidden; }
.lab-bar-fill { display: block; height: 100%; background: var(--site-accent); opacity: .65; }
.lab-col-fused { padding-left: clamp(16px, 3cqw, 32px); border-left: 1px solid var(--lab-rule); }
.lab .lab-col-fused .lab-col-title { color: var(--site-accent); }
.lab-badges { display: block; margin-top: 4px; color: var(--lab-muted); font-size: 11px; line-height: 1.5; }
.floor-demo { margin-top: 20px; padding-top: 12px; border-top: 1px solid var(--lab-rule); }
.floor-strip { position: relative; height: 36px; margin-inline: 3px; border-bottom: 1px solid var(--lab-rule); }
.floor-dot { position: absolute; bottom: 6px; width: 4px; height: 4px; border-radius: 50%; background: var(--lab-muted); opacity: .3; transform: translateX(-50%); }
.floor-dot.pass { background: var(--site-accent); opacity: .85; bottom: 14px; }
.floor-line { position: absolute; top: 0; bottom: -1px; width: 1px; background: var(--site-accent); transform: translateX(-50%); }
.floor-controls { margin-top: 10px; }
.floor-controls input[type="range"] { display: block; width: 100%; min-width: 0; margin: 0; accent-color: var(--site-accent); }
.lab .floor-readout { margin: 10px 0 0; font: 13px/1.5 'Quattrocento Sans', sans-serif; color: var(--lab-muted); }
.lab .floor-empty { margin: 10px 0 0; font-size: 14px; color: var(--lab-muted); }
body.dark-mode .lab { --lab-ink: #dedbd6; --lab-rule: rgb(255 255 255 / 16%); background: rgb(255 255 255 / 3%); }
@media (hover: hover) {
  .lab-hit-title:hover { color: var(--site-accent); }
  .lab-hit-title:hover .site-arrow-up-right { opacity: 1; transform: translate(1px, -1px); }
  .lab-go:hover:not(:disabled) { border-color: var(--site-accent); color: var(--site-accent); }
  .lab-chip:hover:not(:disabled) { color: var(--site-accent); }
}
@media (prefers-reduced-motion: reduce) { .lab-hit-title .site-arrow-up-right { transition: none; } }
@container retrieval-lab (max-width: 640px) {
  .lab-columns { grid-template-columns: 1fr; gap: 24px; }
  .lab-col-fused { padding: 20px 0 0; border-left: 0; border-top: 1px solid var(--lab-rule); }
  .lab-col ol { display: grid; grid-template-columns: 1fr; }
}
</style>
